<!-- TimeseriesWidget with Dashboard-managed streaming data -->
<script setup>
import { ref, onMounted, onUnmounted, watch, computed, defineProps, defineEmits, nextTick } from 'vue';
import Plotly from 'plotly.js-dist';

// Parse API timestamp as UTC (format YYYY-MM-DD HH:MM:SS or ISO). Ensures trace times match Plotly axis (UTC ms). (format YYYY-MM-DD HH:MM:SS or ISO). Ensures trace times match Plotly axis (UTC ms).
function parseUTC(s) {
  if (!s) return null;
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(str) || str.endsWith('Z')) return new Date(str);
  const utcStr = str.replace(' ', 'T') + 'Z';
  return new Date(utcStr);
}

/**
 * Given parallel x (Date[]) and y arrays, inserts null sentinels between any two consecutive
 * points whose timestamps are more than gapThresholdMs apart. Plotly skips drawing a line
 * segment when it encounters a null y-value (requires connectgaps: false on the trace).
 */
function applyGapThreshold(timestamps, values, gapThresholdMs) {
  if (!gapThresholdMs || gapThresholdMs <= 0 || timestamps.length < 2) {
    return { x: timestamps, y: values };
  }
  const x = [];
  const y = [];
  for (let i = 0; i < timestamps.length; i++) {
    if (i > 0) {
      const delta = timestamps[i].getTime() - timestamps[i - 1].getTime();
      if (delta > gapThresholdMs) {
        // Insert a null break midway between the two points
        const midMs = timestamps[i - 1].getTime() + delta / 2;
        x.push(new Date(midMs));
        y.push(null);
      }
    }
    x.push(timestamps[i]);
    y.push(values[i]);
  }
  return { x, y };
}

function getDefaultXAxisRange() {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return [twentyFourHoursAgo.getTime(), now.getTime()];
}

import { BRAND, getThemeLayout } from '@/components/widgets/plotlyBrand';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';

const isDark = () => document.documentElement.classList.contains('dark');

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  historicalData: {
    type: Object,
    default: () => ({})
  },
  syncXAxis: {
    type: Object,
    default: null // { range: [min, max], revision: number }
  },
  fullXAxisRange: {
    type: Object,
    default: null // { range: [min, max], revision: number } - the unzoomed "full" range
  },
  crosshairShow: {
    type: Boolean,
    default: false
  },
  crosshairPosition: {
    type: Number,
    default: 0
  },
  crosshairContainerLeft: {
    type: Number,
    default: 0
  },
  crosshairViewportX: {
    type: Number,
    default: 0
  },
  historicalLoading: {
    type: Object,
    default: () => ({})
  },
  isLiveMode: {
    type: Boolean,
    default: true
  },
  editMode: {
    type: Boolean,
    default: false
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['xAxisChange']);

// State
const plotContainer = ref(null);
const loading = ref(true);
let darkModeObserver = null;
let plotResizeObserver = null;
const error = ref(null);
const plotData = ref([]);
const layout = ref(null);
const config = ref(null);
const lastKnownByLineId = ref({}); // { [lineId]: { value, timestamp, unit } } for legend when no data in range
const isInternalUpdate = ref(false); // Flag to prevent feedback loop
const userHasZoomed = ref(false); // Show "Reset view" only after user zooms/pans
const crosshairValues = ref(null); // { x: Date, pixelX: number, values: [{ name, value, unit }] }
const showLegendDropdown = ref(false);

function toRangeMs(range) {
  if (!Array.isArray(range) || range.length !== 2) return null;
  const [start, end] = range;
  const startMs = start instanceof Date ? start.getTime() : new Date(start).getTime();
  const endMs = end instanceof Date ? end.getTime() : new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  return [startMs, endMs];
}

function rangesAreEqual(rangeA, rangeB, toleranceMs = 1) {
  const a = toRangeMs(rangeA);
  const b = toRangeMs(rangeB);
  if (!a || !b) return false;
  return Math.abs(a[0] - b[0]) <= toleranceMs && Math.abs(a[1] - b[1]) <= toleranceMs;
}

// Legend items derived from plotData for the custom mobile dropdown
const legendItems = computed(() => {
  return plotData.value.map(line => {
    if (line.isNotFound) {
      return {
        id: line.id,
        name: line.friendlyName || line.id,
        color: line.line?.color || '#888',
        isNotFound: true,
        notFoundSource: line.source,
        lastValue: null,
        lastTimestamp: null,
      };
    }
    const lastKnown = lastKnownByLineId.value[line.id];
    return {
      id: line.id,
      name: line.friendlyName || line.id,
      color: line.line?.color || '#888',
      isNotFound: false,
      notFoundSource: null,
      lastValue: lastKnown ? `${lastKnown.value} ${lastKnown.unit || ''}`.trim() : 'N/A',
      lastTimestamp: lastKnown?.timestamp || null,
    };
  });
});

// // Computed - get list of all sources needed
// const requiredSources = computed(() => {
//   if (!props.widget.specifications) return [];
//   return Object.keys(props.widget.specifications);
// });

const activeSpecifications = computed(() => {
  if (!props.widget.specifications) return {};
  return Object.fromEntries(
    Object.entries(props.widget.specifications)
      .filter(([source]) => props.sourceErrors?.[source]?.type !== 'not_found')
  );
});

const notFoundSpecSources = computed(() =>
  Object.keys(props.widget.specifications || {}).filter(
    s => props.sourceErrors?.[s]?.type === 'not_found'
  )
);

const applyTheme = () => {
  if (!layout.value) return;
  const theme = getThemeLayout(isDark());
  layout.value = {
    ...layout.value,
    ...theme,
    title: {
      ...layout.value.title,
      font: { ...layout.value.title?.font, color: theme.font.color },
    },
    xaxis: {
      ...layout.value.xaxis,
      ...theme.xaxis,
      rangeselector: { ...layout.value.xaxis?.rangeselector, ...theme.xaxis.rangeselector },
      rangeslider: { ...layout.value.xaxis?.rangeslider, ...theme.xaxis.rangeslider },
    },
    yaxis: { ...layout.value.yaxis, ...theme.yaxis },
    legend: { ...layout.value.legend, ...theme.legend },
  };
  updatePlot();
};

// Lifecycle
onMounted(() => {
  initializePlot();
  setupPlotLines();
  loading.value = false; // Dashboard owns data loading; we just render

  darkModeObserver = new MutationObserver(applyTheme);
  darkModeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

  // Reflow Plotly canvas whenever the container is resized (e.g. device rotation)
  if (plotContainer.value) {
    plotResizeObserver = new ResizeObserver(() => {
      if (plotContainer.value) Plotly.Plots.resize(plotContainer.value);
    });
    plotResizeObserver.observe(plotContainer.value);
  }
});

onUnmounted(() => {
  if (darkModeObserver) {
    darkModeObserver.disconnect();
  }
  if (plotResizeObserver) {
    plotResizeObserver.disconnect();
  }
  if (plotContainer.value) {
    Plotly.purge(plotContainer.value);
  }
});

// Watch for streaming data updates (only in live mode)
watch(() => props.streamData, (newData) => {
  if (!props.isLiveMode) return;
  updateWithStreamData(newData);
}, { deep: true });

// Watch for config or historical data from dashboard
watch([() => props.widget.specifications, () => props.historicalData, () => props.sourceErrors], () => {
  plotData.value = [];
  lastKnownByLineId.value = {};
  setupPlotLines();
}, { deep: true });

// Keep Plotly title in sync when chart title changes from edit modal.
watch(() => props.widget.title, (newTitle) => {
  if (!layout.value || !plotContainer.value) return;
  layout.value.title = {
    ...(layout.value.title || {}),
    text: newTitle || 'Time Series'
  };
  updatePlot();
});

// Watch for external x-axis sync changes
watch(() => props.syncXAxis, (newSync) => {
  if (newSync && newSync.range && !isInternalUpdate.value && layout.value) {
    layout.value.xaxis.range = newSync.range;
    layout.value.xaxis.autorange = false;
    if (layout.value.xaxis.rangeslider) {
      // Set rangeslider to the synced range so it shows the full queried period as context
      layout.value.xaxis.rangeslider.range = newSync.range;
      layout.value.xaxis.rangeslider.autorange = false;
    }
    if (newSync.isFullReset) {
      userHasZoomed.value = false;
    } else if (!props.isLiveMode) {
      userHasZoomed.value = !rangesAreEqual(newSync.range, props.fullXAxisRange?.range);
    }
    // In live mode (without a full reset), don't infer zoom state from range comparison —
    // auto-scroll updates would incorrectly trigger "userHasZoomed". Only the user's own
    // plotly_relayout event (or an explicit reset) should change this flag.
    updatePlot();
  }
}, { deep: true });

// Compute crosshair intersection values when crosshair is shown
// Uses Plotly's d2p (data-to-pixel) so we find the point whose *drawn* position is nearest the crosshair.
function getYAtXNearestByPixel(trace, xaxis, pixelInAxis) {
  const xArr = trace.x || [];
  const yArr = trace.y || [];
  if (!xArr.length || !yArr.length || typeof xaxis.d2p !== 'function') return null;

  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < xArr.length; i++) {
    const v = xArr[i];
    const pixelPos = xaxis.d2p(v instanceof Date ? v : new Date(v));
    if (pixelPos == null || !Number.isFinite(pixelPos)) continue;
    const d = Math.abs(pixelPos - pixelInAxis);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return yArr[bestIdx];
}

function updateCrosshairValues() {
  crosshairValues.value = null;
  if (!props.crosshairShow || !plotContainer.value || !plotData.value.length) return;
  const el = plotContainer.value;
  const fullLayout = el._fullLayout;
  if (!fullLayout || !fullLayout.xaxis) return;
  const xaxis = fullLayout.xaxis;
  const plotRect = el.getBoundingClientRect();
  // Prefer actual line position (crosshairViewportX) when set; otherwise derive from container + position
  const crosshairViewportX = props.crosshairViewportX > 0
    ? props.crosshairViewportX
    : props.crosshairContainerLeft + props.crosshairPosition;
  const pixelXInPlot = crosshairViewportX - plotRect.left;
  // Use layout coordinates: scale div pixels to layout pixels
  let pixelXLayout = pixelXInPlot;
  if (fullLayout.width != null && plotRect.width > 0) {
    pixelXLayout = pixelXInPlot * (fullLayout.width / plotRect.width);
  }
  const _offset = xaxis._offset;
  const _length = xaxis._length;
  if (_offset == null || _length == null || _length <= 0) return;
  // Clamp to axis span
  const pixelInAxis = pixelXLayout - _offset;
  if (pixelInAxis < 0 || pixelInAxis > _length) return;
  // Compute time from axis range the same way Plotly does (linear mapping)
  const r2l = xaxis.r2l;
  if (typeof r2l !== 'function') return;
  const r0 = r2l(xaxis.range[0]);
  const r1 = r2l(xaxis.range[1]);
  if (r0 == null || r1 == null || !Number.isFinite(r0) || !Number.isFinite(r1)) return;
  const frac = pixelInAxis / _length;
  const dataMs = r0 + frac * (r1 - r0);
  const xDate = new Date(dataMs);
  if (Number.isNaN(xDate.getTime())) return;
  // Find y by which point is nearest in *pixel* space (Plotly's d2p) so we match what's drawn
  const values = [];
  const validPlotData = plotData.value.filter(line => line.x.length > 0 && line.y.length > 0);
  for (const trace of validPlotData) {
    const y = getYAtXNearestByPixel(trace, xaxis, pixelInAxis);
    if (y !== null && y !== undefined) {
      values.push({
        name: trace.friendlyName || trace.name,
        value: typeof y === 'number' && !Number.isInteger(y) ? y.toFixed(2) : String(y),
        unit: trace.unit || ''
      });
    }
  }
  if (values.length) {
    crosshairValues.value = { x: xDate, pixelX: pixelXInPlot, values };
  }
}

let crosshairRafId = null;

watch(
  () => [props.crosshairShow, props.crosshairPosition, props.crosshairContainerLeft, props.crosshairViewportX, plotData],
  () => {
    if (!props.crosshairShow) {
      crosshairValues.value = null;
      if (crosshairRafId !== null) cancelAnimationFrame(crosshairRafId);
      crosshairRafId = null;
      return;
    }
    if (crosshairRafId !== null) return;
    crosshairRafId = requestAnimationFrame(() => {
      crosshairRafId = null;
      nextTick(() => updateCrosshairValues());
    });
  },
  { deep: true }
);

// True while any source required by this widget is still being fetched
const isLoading = computed(() => {
  if (!activeSpecifications.value) return false;
  return Object.keys(activeSpecifications.value).some(source => !!props.historicalLoading[source]);
});

// Crosshair time label: use UTC to match Plotly's x-axis tick labels (data/range are UTC ms)
const crosshairTimeLabel = computed(() => {
  if (!crosshairValues.value?.x) return '';
  const d = crosshairValues.value.x;
  const hour12 = (props.timeFormat || '24hour').toLowerCase() === '12hour';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium', timeZone: 'UTC', hour12 });
});

// Methods
const initializePlot = () => {
  if (!plotContainer.value) return;
  
  const isMobile = window.innerWidth < 640;
  const defaultRange = props.syncXAxis?.range ?? getDefaultXAxisRange();

  const theme = getThemeLayout(isDark());
  layout.value = {
    showlegend: !isMobile,
    ...theme,
    legend: {
      x: 0,
      y: -1,
      orientation: 'h',
      ...theme.legend,
    },
    title: {
      text: props.widget.title || 'Time Series',
      font: {
        size: isMobile ? 20 : 25,
        weight: 'bold',
        family: BRAND.fontDisplay,
        color: theme.font.color,
      }
    },
    xaxis: {
      range: defaultRange,
      autorange: false,
      mirror: true,
      ticks: 'outside',
      showline: true,
      type: 'date',
      ...theme.xaxis,
      rangeselector: {
        visible: !isMobile,
        buttons: [
          { count: 24, label: '24hr', step: 'hour', stepmode: 'backward' },
          { count: 12, label: '12hr', step: 'hour', stepmode: 'backward' },
          { count: 30, label: '30min', step: 'minute', stepmode: 'backward' },
          { count: 10, label: '10min', step: 'minute', stepmode: 'backward' },
        ],
        ...theme.xaxis.rangeselector,
      },
      rangeslider: {
        visible: !isMobile,
        range: defaultRange,
        autorange: false,
        borderwidth: 1,
        ...theme.xaxis.rangeslider,
      },
    },
    yaxis: {
      autorange: true,
      mirror: true,
      ticks: 'outside',
      showline: true,
      type: 'linear',
      ...theme.yaxis,
    },
    margin: {
      l: isMobile ? 40 : 50,
      r: isMobile ? 40 : 50,
      t: isMobile ? 80 : 100,
      b: isMobile ? 60 : 100,
      pad: 4
    },
    autosize: true,
  };
  
  config.value = {
    displaylogo: false,
    responsive: true,
    displayModeBar: !isMobile,
    modeBarButtonsToRemove: [
      'select2d', 'lasso2d', 'resetScale2d',
      'zoom2d', 'pan2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'toImage'
    ]
  };
  
  Plotly.newPlot(plotContainer.value, [], layout.value, config.value);
  
  // Listen for plotly_relayout events (zoom, pan, etc.)
  plotContainer.value.on('plotly_relayout', (eventData) => {
    // Use the current synced range as the rangeslider context window (falls back to default 24h)
    const contextRange = props.syncXAxis?.range ?? getDefaultXAxisRange();

    // Track y-axis range changes so layout.value stays in sync with Plotly's internal state.
    // Without this, Plotly.react calls (e.g. stream updates) would restore a stale Y range.
    if (eventData['yaxis.range[0]'] != null && eventData['yaxis.range[1]'] != null) {
      if (layout.value?.yaxis) {
        layout.value.yaxis.range = [eventData['yaxis.range[0]'], eventData['yaxis.range[1]']];
        layout.value.yaxis.autorange = false;
      }
    } else if (eventData['yaxis.autorange']) {
      if (layout.value?.yaxis) {
        layout.value.yaxis.autorange = true;
        delete layout.value.yaxis.range;
      }
    }

    // Check if this is an x-axis range change (from user interaction, not our programmatic update)
    if (eventData['xaxis.range[0]'] && eventData['xaxis.range[1]']) {
      const newRange = [eventData['xaxis.range[0]'], eventData['xaxis.range[1]']];
      if (!isInternalUpdate.value) userHasZoomed.value = true;
      isInternalUpdate.value = true;
      // Keep rangeslider at the full context range so it shows "where I'm zoomed" in context
      if (layout.value?.xaxis?.rangeslider) {
        layout.value.xaxis.rangeslider.range = contextRange;
        layout.value.xaxis.rangeslider.autorange = false;
      }
      emit('xAxisChange', { range: newRange, revision: Date.now() });
      // Reset flag after a short delay
      setTimeout(() => {
        isInternalUpdate.value = false;
      }, 50);
    } else if (eventData['xaxis.range']) {
      // Handle the case where range is set as an array
      const newRange = eventData['xaxis.range'];
      if (!isInternalUpdate.value) userHasZoomed.value = true;
      isInternalUpdate.value = true;
      if (layout.value?.xaxis?.rangeslider) {
        layout.value.xaxis.rangeslider.range = contextRange;
        layout.value.xaxis.rangeslider.autorange = false;
      }
      emit('xAxisChange', { range: newRange, revision: Date.now() });
      setTimeout(() => {
        isInternalUpdate.value = false;
      }, 50);
    } else if (eventData['xaxis.autorange']) {
      // User clicked autoscale - emit this so all charts reset
      isInternalUpdate.value = true;
      emit('xAxisChange', { range: null, revision: Date.now(), autorange: true });
      setTimeout(() => {
        isInternalUpdate.value = false;
      }, 50);
    }
  });
};

const setupPlotLines = () => {
  plotData.value = [];

  // Create plot lines for each specification
  Object.entries(activeSpecifications.value).forEach(([source, specifications]) => {
    const sourceData = (props.historicalData && props.historicalData[source]) || [];
    
    specifications.forEach((spec, index) => {
      const lineId = `${source}-${index}`;
      const friendlyName = spec.friendlyName || spec.field;
      
      // Last known datapoint for legend (from historical data, latest doc that has the field)
      const docWithField = sourceData.reduce((best, d) => {
        if (d[spec.field] === undefined || !d.messageUTC) return best;
        const t = parseUTC(d.messageUTC)?.getTime();
        if (!t || Number.isNaN(t)) return best;
        if (!best) return d;
        return (parseUTC(best.messageUTC)?.getTime() || 0) < t ? d : best;
      }, null);
      if (docWithField) {
        let v = docWithField[spec.field];
        if (typeof v === 'boolean') v = v ? 1 : 0;
        lastKnownByLineId.value[lineId] = {
          value: v,
          timestamp: docWithField.messageUTC,
          unit: spec.unit || ''
        };
      }
      
      // Build timestamps and values from all fetched historical data
      const rawTimestamps = [];
      const rawValues = [];
      
      sourceData.forEach(doc => {
        if (doc.messageUTC && doc[spec.field] !== undefined) {
          const timestamp = parseUTC(doc.messageUTC);
          if (!timestamp || Number.isNaN(timestamp.getTime())) return;
          
          rawTimestamps.push(timestamp);
          
          let value = doc[spec.field];
          if (typeof value === 'boolean') {
            value = value ? 1 : 0;
          }
          rawValues.push(value);
        }
      });

      const gapThresholdMs = (spec.dataFrequencySeconds ?? 0) * 1000;
      const { x: timestamps, y: values } = applyGapThreshold(rawTimestamps, rawValues, gapThresholdMs);
      
      // Legend text: match format of charts with data (Last timestamp / Last value)
      const lastKnown = lastKnownByLineId.value[lineId];
      const legendName = lastKnown
        ? `<b>${friendlyName}</b><br>Last timestamp: ${lastKnown.timestamp}<br>Last value: ${lastKnown.value} ${lastKnown.unit || ''}`
        : `<b>${friendlyName}</b><br>Last timestamp: N/A<br>Last value: N/A`;
      
      // Create plot line (x,y only in-range points; name always for legend)
      const plotLine = {
        id: lineId,
        source: source,
        field: spec.field,
        name: legendName,
        friendlyName: friendlyName,
        unit: spec.unit || '',
        dataFrequencySeconds: spec.dataFrequencySeconds ?? 0,
        x: timestamps,
        y: values,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: spec.color },
        marker: { 
          size: 6,
          symbol: 'circle',
          maxdisplayed: 50,
        },
        connectgaps: false,
        hovertemplate: `DATE: %{x}<br>VALUE: %{y} ${spec.unit || ''}<extra></extra>`
      };
      
      // If we have in-range data, show last value in legend; otherwise keep lastKnown/No Data
      if (timestamps.length > 0 && values.length > 0) {
        const lastValue = values[values.length - 1];
        const lastTimestamp = timestamps[timestamps.length - 1].toISOString();
        plotLine.name = `<b>${friendlyName}</b><br>Last timestamp: ${lastTimestamp}<br>Last value: ${lastValue} ${spec.unit || ''}`;
      }
      
      plotData.value.push(plotLine);
    });
  });

  // Not-found sources: add a legendonly placeholder per field so the legend
  // still lists them with a "Source X not found" label. We give them x:[0], y:[null]
  // so updatePlot's hasData branch preserves visible:'legendonly' untouched.
  notFoundSpecSources.value.forEach(source => {
    const specs = props.widget.specifications[source] || [];
    specs.forEach((spec, index) => {
      const friendlyName = spec.friendlyName || spec.field;
      plotData.value.push({
        id: `${source}-${index}-notfound`,
        source,
        field: spec.field,
        friendlyName,
        unit: spec.unit || '',
        isNotFound: true,
        visible: 'legendonly',
        name: `<b>${friendlyName}</b><br>Source "${source}" not found`,
        x: [0],
        y: [null],
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: spec.color, width: 0 },
        marker: { size: 0 },
        connectgaps: false,
        hovertemplate: '',
      });
    });
  });

  updatePlot();
};

const updateWithStreamData = (streamData) => {
  if (!streamData || plotData.value.length === 0) return;
  
  // Calculate timestamp for 24 hours ago
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  // Update each plot line with new streaming data
  plotData.value.forEach(plotLine => {
    if (plotLine.isNotFound) return;
    const sourceStream = streamData[plotLine.source];
    
    if (sourceStream?.lastValue) {
      const newValue = sourceStream.lastValue[plotLine.field];
      const newTimestamp = sourceStream.lastValue.messageUTC;
      
      if (newValue !== undefined && newTimestamp) {
        const newDate = parseUTC(newTimestamp);
        if (!newDate || Number.isNaN(newDate.getTime())) return;
        
        // Always update last known for legend (even if point is outside current range)
        const displayValue = typeof newValue === 'boolean' ? (newValue ? 1 : 0) : newValue;
        lastKnownByLineId.value[plotLine.id] = {
          value: displayValue,
          timestamp: newTimestamp,
          unit: plotLine.unit || ''
        };
        
        // Check if this is a new data point (not already in the array)
        const lastTimestamp = plotLine.x[plotLine.x.length - 1];
        if (!lastTimestamp || newDate.getTime() > lastTimestamp.getTime()) {
          // Insert a null break if the gap exceeds the threshold
          const gapThresholdMs = (plotLine.dataFrequencySeconds ?? 0) * 1000;
          if (gapThresholdMs > 0 && lastTimestamp) {
            const lastRealTimestamp = lastTimestamp instanceof Date ? lastTimestamp : new Date(lastTimestamp);
            const delta = newDate.getTime() - lastRealTimestamp.getTime();
            if (delta > gapThresholdMs) {
              const midMs = lastRealTimestamp.getTime() + delta / 2;
              plotLine.x.push(new Date(midMs));
              plotLine.y.push(null);
            }
          }

          // Add new data point
          plotLine.x.push(newDate);
          plotLine.y.push(displayValue);
          
          // Remove data points older than 24 hours
          while (plotLine.x.length > 0 && plotLine.x[0] < twentyFourHoursAgo) {
            plotLine.x.shift();
            plotLine.y.shift();
          }
          
          plotLine.name = `<b>${plotLine.friendlyName}</b><br>Last timestamp: ${newTimestamp}<br>Last value: ${displayValue} ${plotLine.unit || ''}`;
        }
      }
    }
  });

  // Refresh legend for lines with no in-range points (same format as other charts)
  plotData.value.forEach(plotLine => {
    if (plotLine.isNotFound) return;
    if (plotLine.x.length > 0 && plotLine.y.length > 0) return;
    const lastKnown = lastKnownByLineId.value[plotLine.id];
    plotLine.name = lastKnown
      ? `<b>${plotLine.friendlyName}</b><br>Last timestamp: ${lastKnown.timestamp}<br>Last value: ${lastKnown.value} ${lastKnown.unit || ''}`
      : `<b>${plotLine.friendlyName}</b><br>Last timestamp: N/A<br>Last value: N/A`;
  });

  // Do not reset x-axis range when new stream data arrives - preserve user's zoom/pan.
  // (Previously we emitted xAxisChange and set layout.xaxis.range to default 24h here.)

  // When new data was added and its time is past the current view's right edge, slide the
  // range so the new point is visible (chart "follows" the stream).
  let maxTime = null;
  plotData.value.forEach(plotLine => {
    if (plotLine.x?.length) {
      const t = plotLine.x[plotLine.x.length - 1];
      const ms = t instanceof Date ? t.getTime() : t;
      if (ms != null && Number.isFinite(ms)) maxTime = maxTime == null ? ms : Math.max(maxTime, ms);
    }
  });
  if (maxTime != null && layout.value?.xaxis?.range) {
    const [r0, r1] = layout.value.xaxis.range;
    if (r0 != null && r1 != null && maxTime > r1) {
      const width = r1 - r0;
      const newRange = [maxTime - width, maxTime];
      layout.value.xaxis.range = newRange;
      layout.value.xaxis.autorange = false;
      if (layout.value.xaxis.rangeslider) {
        layout.value.xaxis.rangeslider.range = getDefaultXAxisRange();
        layout.value.xaxis.rangeslider.autorange = false;
      }
      isInternalUpdate.value = true;
      emit('xAxisChange', { range: newRange, revision: Date.now() });
      setTimeout(() => { isInternalUpdate.value = false; }, 50);
    }
  }

  updatePlot();
};

const updatePlot = () => {
  if (!plotContainer.value) return;
  
  const range = layout.value?.xaxis?.range;
  const rangeStart = range && range[0] != null ? range[0] : Date.now() - 24 * 60 * 60 * 1000;
  
  // Always pass all series; for empty traces add one invisible point so Plotly shows legend
  // Copy x/y arrays so Plotly sees new references and redraws when stream data updates.
  // Use numeric timestamps for x so Plotly's diff reliably detects changes.
  const tracesToPlot = plotData.value.map(line => {
    const hasData = (line.x?.length || 0) > 0 && (line.y?.length || 0) > 0;
    if (hasData) {
      const xNum = (line.x || []).map(v => v instanceof Date ? v.getTime() : v);
      return { ...line, x: xNum, y: [...(line.y || [])], name: line.name };
    }
    // Empty trace: add dummy point and show only in legend (no line/marker drawn)
    return {
      ...line,
      x: [rangeStart],
      y: [line.y?.[0] ?? 0],
      name: line.name,
      visible: 'legendonly',
      line: { ...line.line, width: 0 },
      marker: { ...line.marker, size: 0 }
    };
  });
  
  Plotly.react(plotContainer.value, tracesToPlot, { ...layout.value }, config.value);
  if (props.crosshairShow) {
    nextTick(updateCrosshairValues);
  }
};

async function resetXAxisToFullView() {
  // In live mode the "full view" is always the current live window, not the range frozen at
  // page load. History mode uses the user-selected date range stored in fullXAxisRange.
  const newRange = props.isLiveMode
    ? getDefaultXAxisRange()
    : (props.fullXAxisRange?.range ?? props.syncXAxis?.range ?? getDefaultXAxisRange());
  const normalizedRange = toRangeMs(newRange);
  if (!layout.value?.xaxis) return;
  if (!normalizedRange) return;
  layout.value.xaxis.range = normalizedRange;
  layout.value.xaxis.autorange = false;
  if (layout.value.xaxis.rangeslider) {
    layout.value.xaxis.rangeslider.range = normalizedRange;
    layout.value.xaxis.rangeslider.autorange = false;
  }
  if (layout.value.yaxis) {
    layout.value.yaxis.autorange = true;
    delete layout.value.yaxis.range;
  }
  isInternalUpdate.value = true;

  if (plotContainer.value) {
    const relayoutUpdate = {
      'xaxis.range': normalizedRange,
      'xaxis.autorange': false,
      'yaxis.autorange': true,
    };
    if (layout.value.xaxis.rangeslider) {
      relayoutUpdate['xaxis.rangeslider.range'] = normalizedRange;
      relayoutUpdate['xaxis.rangeslider.autorange'] = false;
    }
    await Plotly.relayout(plotContainer.value, relayoutUpdate);
  }

  emit('xAxisChange', { range: normalizedRange, revision: Date.now(), isFullReset: true });
  setTimeout(() => { isInternalUpdate.value = false; }, 50);
  userHasZoomed.value = false;
  updatePlot();
}

function downloadChartAsPng() {
  if (!plotContainer.value) return;
  const el = plotContainer.value;
  const rect = el.getBoundingClientRect();
  const width = Math.floor(rect.width);
  const height = Math.floor(rect.height);
  Plotly.toImage(el, { format: 'png', width, height })
    .then((dataUrl) => {
      const link = document.createElement('a');
      link.download = `${props.widget?.title || 'timeseries'}.png`.replace(/[^a-zA-Z0-9._-]/g, '_');
      link.href = dataUrl;
      link.click();
    })
    .catch((err) => console.error('Chart export failed:', err));
}
</script>

<template>
  <div class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm p-2 sm:p-5 h-fit mb-3 sm:mb-5 mr-2 sm:mr-3 w-full relative">
    <div class="h-[3px] bg-mc-red -mx-2 sm:-mx-5 -mt-2 sm:-mt-5 mb-2 sm:mb-5 shrink-0"></div>
    <!-- Error State -->
    <div v-if="error" class="absolute inset-0 top-[30px] left-[50px] sm:left-[100px] right-[50px] sm:right-[100px] bottom-[100px] flex items-center justify-center z-20">
      <div class="text-lg sm:text-2xl font-bold text-mc-red">{{ error }}</div>
    </div>
    
    <!-- Loading State -->
    <div v-else-if="isLoading" class="absolute inset-0 flex items-center justify-center z-20 rounded-mc-sm bg-mc-white/60 dark:bg-mc-ink/60 backdrop-blur-sm">
      <LoadingAnimation3 size="50px" />
    </div>
    
    <div class="w-full relative">
      <!-- Chart actions: Download PNG and Back to full view (hidden during edit mode) -->
      <div v-if="!editMode" class="absolute top-0 right-0 z-10 flex justify-end items-center gap-1 p-1">
        <button
          v-if="userHasZoomed"
          type="button"
          class="text-xs sm:text-sm px-2 py-1.5 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark hover:bg-mc-cloud dark:hover:bg-mc-ink shadow-mc-sm transition-colors duration-160"
          @click="resetXAxisToFullView"
        >
          Back to full view
        </button>
        <button
          type="button"
          class="text-xs sm:text-sm px-2 py-1.5 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark hover:bg-mc-cloud dark:hover:bg-mc-ink shadow-mc-sm flex items-center gap-1 transition-colors duration-160"
          title="Download as PNG"
          @click="downloadChartAsPng"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span class="hidden sm:inline">Download PNG</span>
        </button>
      </div>
      <!-- Plotly container with responsive height -->
      <div ref="plotContainer" class="w-full h-[300px] sm:h-[400px] md:h-[500px]"></div>

      <!-- Mobile Legend Dropdown (hidden on sm+) -->
      <div v-if="legendItems.length" class="sm:hidden mt-2 relative">
        <button
          type="button"
          class="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark shadow-mc-sm"
          @click="showLegendDropdown = !showLegendDropdown"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-ink-muted dark:text-on-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          <span class="flex-1 text-left">Legend</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-ink-muted dark:text-on-dark-muted transition-transform duration-200"
            :class="showLegendDropdown ? 'rotate-180' : ''"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div
          v-if="showLegendDropdown"
          class="mt-1 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal shadow-mc-sm divide-y divide-mc-steel dark:divide-line-on-dark"
        >
          <div
            v-for="item in legendItems"
            :key="item.id"
            class="flex items-start gap-3 px-3 py-2"
          >
            <!-- Color swatch -->
            <span class="mt-0.5 flex-shrink-0 w-3 h-3 rounded-full border border-mc-steel dark:border-line-on-dark" :style="{ backgroundColor: item.color }"></span>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-mc-ink dark:text-on-dark truncate">{{ item.name }}</p>
              <p v-if="item.isNotFound" class="text-xs text-mc-red">Source "{{ item.notFoundSource }}" not found</p>
              <template v-else>
                <p class="text-xs text-ink-muted dark:text-on-dark-muted">Last value: <span class="font-medium text-mc-ink dark:text-on-dark">{{ item.lastValue }}</span></p>
                <p v-if="item.lastTimestamp" class="text-xs text-ink-muted dark:text-on-dark-muted">{{ item.lastTimestamp }}</p>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- Crosshair value tooltip - shown at crosshair line on this chart -->
      <div
        v-if="crosshairValues"
        class="absolute top-1 left-0 z-50 pointer-events-none text-xs sm:text-sm font-mono bg-mc-ink text-on-dark rounded-mc-sm px-2 py-1.5 shadow-mc-md whitespace-nowrap max-w-[90vw]"
        :style="{ left: `${crosshairValues.pixelX}px`, transform: 'translateX(-50%)' }"
      >
        <div class="text-on-dark-muted border-b border-line-on-dark pb-0.5 mb-0.5">
          {{ crosshairTimeLabel }}
        </div>
        <div v-for="v in crosshairValues.values" :key="v.name" class="flex gap-2">
          <span class="text-on-dark-muted">{{ v.name }}:</span>
          <span class="font-semibold">{{ v.value }}{{ v.unit ? ` ${v.unit}` : '' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure Plotly container fills available space */
:deep(.plotly) {
  width: 100% !important;
  height: 100% !important;
}
</style>
